/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { useActivation } from '@context/MobileOne';
import { usePayment } from '@context/Payment';
import { useA11y } from '@context/Utils';
import {
  ButtonPrimary,
  CardInfobox,
  CardSupport,
  Link,
  NavigationPageTitle,
  Ta11yText,
  ScrollToTop,
  ButtonCancelActivation
} from '@core/index';
import Icons from '@core/Utils/Icons/Icons';
import { useStaticContent } from '@context/StaticContent';
import { AppPageContentWrapper } from '@part/AppPageContentWrapper';
import { replaceDynamicCmsContent, tA11y } from '@utils/a11y/a11yHelpers';
import {
  appButtonType,
  appCardStyle,
  appIconsPath,
  appInfoCardSize,
  appLinkSize,
  appLinkStyle,
  parseToGermanPrice,
  appMpsPaymentMethodType
} from '@utils/globalConstant';
import moment from 'moment';

export function OverviewData() {
  // Contexts
  const { tA11yTranslate, announce } = useA11y();
  const {
    isLoading,
    setStep9SubStep,
    countries,
    simProvider,
    withMNP,
    tariffActivationForm,
    setTariffActivationForm,
    phoneNumberActivationForm,
    setPhoneNumberActivationForm,
    personalDataForm,
    setPersonalDataForm,
    legitimationMethods,
    getTariffName,
    withAutoTopup,
    setWithAutoTopup
  } = useActivation();
  const { staticContentData } = useStaticContent();
  const { mpsActivationTopupPayment, setActivationMpsTopupPayment } = usePayment();

  // Local state for delete notification
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

  // Recover data from sessionStorage if context is empty (after PayPal redirect)
  const [recoveredData, setRecoveredData] = useState({
    tariffActivationForm: null,
    phoneNumberActivationForm: null,
    personalDataForm: null
  });

  useEffect(() => {
    // console.log('[OverviewData] 🟠 Recovery effect running...');
    // console.log('[OverviewData] Current context state:', {
    //   chosenTariffId: tariffActivationForm?.chosenTariffId,
    //   tariffPrice: tariffActivationForm?.tariffPrice,
    //   firstName: personalDataForm?.firstName,
    //   msisdn: phoneNumberActivationForm?.msisdn
    // });

    const isContextEmpty =
      !tariffActivationForm?.chosenTariffId && !tariffActivationForm?.tariffPrice;
    // console.log('[OverviewData] isContextEmpty:', isContextEmpty);

    if (isContextEmpty) {
      // console.log('[OverviewData] 🟡 Context is empty - attempting to recover from sessionStorage');
      try {
        const storedTariff = sessionStorage.getItem('mps_activation_tariff');
        const storedPhone = sessionStorage.getItem('mps_activation_phone');
        const storedPersonal = sessionStorage.getItem('mps_activation_personal');
        const storedWithAutoTopup = sessionStorage.getItem('mps_activation_withAutoTopup');

        // console.log('[OverviewData] Raw sessionStorage data found:', {
        //   storedTariff: storedTariff ? '✅ Present' : '❌ Missing',
        //   storedPhone: storedPhone ? '✅ Present' : '❌ Missing',
        //   storedPersonal: storedPersonal ? '✅ Present' : '❌ Missing',
        //   storedWithAutoTopup: storedWithAutoTopup ? '✅ Present' : '❌ Missing'
        // });

        const recovered = {
          tariffActivationForm: storedTariff ? JSON.parse(storedTariff) : null,
          phoneNumberActivationForm: storedPhone ? JSON.parse(storedPhone) : null,
          personalDataForm: storedPersonal ? JSON.parse(storedPersonal) : null,
          withAutoTopup: storedWithAutoTopup ? JSON.parse(storedWithAutoTopup) : false
        };

        // console.log('[OverviewData] ✅ Parsed recovered data:', recovered);

        if (
          recovered.tariffActivationForm ||
          recovered.phoneNumberActivationForm ||
          recovered.personalDataForm
        ) {
          // console.log('[OverviewData] 🟢 Found data in sessionStorage - setting recoveredData state');
          setRecoveredData(recovered);
          // Restore the withAutoTopup flag in context
          if (recovered.withAutoTopup && !withAutoTopup) {
            // console.log('[OverviewData] 🟢 Restoring withAutoTopup=true to context');
            setWithAutoTopup(true);
          }
        } else {
          // console.log('[OverviewData] ⚠️  No valid data found in recovered object');
        }
      } catch (err) {
        // console.error('[OverviewData] ❌ Failed to recover data from sessionStorage:', err);
      }
    } else {
      // console.log('[OverviewData] ✅ Context is populated - no recovery needed');
    }
  }, [
    tariffActivationForm?.chosenTariffId,
    tariffActivationForm?.tariffPrice,
    withAutoTopup,
    setWithAutoTopup
  ]);

  // CRITICAL: Restore recovered data to context for activate-sim call
  // This ensures that when OverviewConsent submits the form, it has all the data
  useEffect(() => {
    if (
      recoveredData.tariffActivationForm ||
      recoveredData.phoneNumberActivationForm ||
      recoveredData.personalDataForm
    ) {
      // console.log('[OverviewData] 🔴 CRITICAL: Restoring recovered data to context for activate-sim');

      if (recoveredData.tariffActivationForm) {
        // console.log('[OverviewData] 🔴 Restoring tariffActivationForm to context:', recoveredData.tariffActivationForm);
        setTariffActivationForm(recoveredData.tariffActivationForm);
      }

      if (recoveredData.phoneNumberActivationForm) {
        // console.log('[OverviewData] 🔴 Restoring phoneNumberActivationForm to context:', recoveredData.phoneNumberActivationForm);
        setPhoneNumberActivationForm(recoveredData.phoneNumberActivationForm);
      }

      if (recoveredData.personalDataForm) {
        // console.log('[OverviewData] 🔴 Restoring personalDataForm to context:', recoveredData.personalDataForm);
        setPersonalDataForm(recoveredData.personalDataForm);
      }

      // console.log('[OverviewData] 🟢 CRITICAL: All recovered data restored to context - activate-sim call will have data!');
    }
  }, [recoveredData, setTariffActivationForm, setPhoneNumberActivationForm, setPersonalDataForm]);

  // CRITICAL: Recover payment result to Payment context for auto-topup display
  useEffect(() => {
    try {
      const storedPaymentResult = sessionStorage.getItem('mps_payment_result');
      const storedAmount = sessionStorage.getItem('mps_payment_amount');
      const storedMethod = sessionStorage.getItem('mps_payment_method');

      // console.log('[OverviewData] 🟡 Checking for payment data in sessionStorage:', {
      //   mps_payment_result: storedPaymentResult ? 'Found' : 'Not found',
      //   mps_payment_amount: storedAmount ? 'Found' : 'Not found',
      //   mps_payment_method: storedMethod ? 'Found' : 'Not found'
      // });
      // console.log('[OverviewData] Current Payment context state BEFORE recovery:', {
      //   amount: mpsActivationTopupPayment.amount,
      //   paymentMethod: mpsActivationTopupPayment.paymentMethod,
      //   lookup_key: mpsActivationTopupPayment.lookup_key,
      //   withAutoTopup
      // });

      // CRITICAL: Don't restore lookup_key if it's been explicitly deleted (withAutoTopup=false and lookup_key=null)
      const isAutoTopupDeleted = !withAutoTopup && mpsActivationTopupPayment.lookup_key === null;
      // console.log('[OverviewData] Guard check - isAutoTopupDeleted:', isAutoTopupDeleted);

      if (isAutoTopupDeleted) {
        // console.log('[OverviewData] 🔴 GUARD: Auto-topup deleted - NOT restoring from sessionStorage');
        return;
      }

      // Always try to restore amount and paymentMethod if they're missing
      // Priority: 1) mps_payment_result, 2) individual keys
      let shouldUpdate = false;
      let updates = {};

      if (
        storedPaymentResult &&
        (!mpsActivationTopupPayment.amount ||
          !mpsActivationTopupPayment.paymentMethod ||
          !mpsActivationTopupPayment.lookup_key)
      ) {
        const parsed = JSON.parse(storedPaymentResult);
        // console.log('[OverviewData] 🔴 CRITICAL: Found mps_payment_result, restoring:', parsed);
        updates = {
          amount: parsed.amount || mpsActivationTopupPayment.amount,
          paymentMethod: parsed.paymentMethod || mpsActivationTopupPayment.paymentMethod,
          lookup_key: parsed.lookup_key || mpsActivationTopupPayment.lookup_key
        };
        shouldUpdate = true;
      } else if (storedAmount || storedMethod) {
        // Fallback: use individual keys if they exist
        if (!mpsActivationTopupPayment.amount || !mpsActivationTopupPayment.paymentMethod) {
          // console.log('[OverviewData] 🟡 FALLBACK: Using individual sessionStorage keys');
          updates = {
            amount: storedAmount || mpsActivationTopupPayment.amount,
            paymentMethod: storedMethod || mpsActivationTopupPayment.paymentMethod
          };
          shouldUpdate = true;
        }
      }

      if (shouldUpdate && Object.keys(updates).length > 0) {
        // console.log('[OverviewData] 🟢 Restoring to Payment context:', updates);
        setActivationMpsTopupPayment((prev) => ({
          ...prev,
          ...updates
        }));
      } else {
        // console.log('[OverviewData] ✅ Payment context already populated or no stored data found');
      }
    } catch (err) {
      // console.error('[OverviewData] Failed to recover payment result:', err);
    }
  }, [
    mpsActivationTopupPayment.amount,
    mpsActivationTopupPayment.paymentMethod,
    mpsActivationTopupPayment.lookup_key,
    withAutoTopup,
    setActivationMpsTopupPayment
  ]);

  // Handle auto-topup deletion
  const handleDeleteAutoTopup = () => {
    // console.log('[OverviewData] 🔴 DELETING AUTO-TOPUP - Current state BEFORE deletion:');
    // console.log({
    //   withAutoTopup,
    //   lookup_key: mpsActivationTopupPayment.lookup_key,
    //   amount: mpsActivationTopupPayment.amount,
    //   paymentMethod: mpsActivationTopupPayment.paymentMethod
    // });

    setWithAutoTopup(false);

    // Clear the redis key (lookup_key) from payment context
    setActivationMpsTopupPayment((prev) => {
      const updated = {
        ...prev,
        lookup_key: null
      };
      // console.log('[OverviewData] 🟡 STATE UPDATE: Setting lookup_key to null');
      // console.log('[OverviewData] State AFTER deletion:');
      // console.log({
      //   lookup_key: updated.lookup_key,
      //   amount: updated.amount,
      //   paymentMethod: updated.paymentMethod
      // });
      return updated;
    });

    setShowDeleteSuccess(true);
    // console.log(
    //   '[OverviewData] 🟢 AUTO-TOPUP DELETED - lookup_key should now be null for activate-sim'
    // );

    // Announce deletion for screen readers
    announce(tA11yTranslate('ek_global_snackbar_autotopup_dlt').content, true);

    // Hide success message after 3 seconds
    setTimeout(() => {
      setShowDeleteSuccess(false);
    }, 3000);
  };

  // Use recovered data as fallback for display
  const displayTariffForm = tariffActivationForm?.chosenTariffId
    ? tariffActivationForm
    : recoveredData.tariffActivationForm || tariffActivationForm;
  const displayPhoneForm = phoneNumberActivationForm?.msisdn
    ? phoneNumberActivationForm
    : recoveredData.phoneNumberActivationForm || phoneNumberActivationForm;
  const displayPersonalForm = personalDataForm?.firstName
    ? personalDataForm
    : recoveredData.personalDataForm || personalDataForm;

  // const
  const overviewHeader2Content = tA11y('ek_reg_summary_hdl2');
  const overviewHeader3Content = tA11y('ek_reg_summary_hdl3');
  const overviewHeader4Content = tA11y('ek_reg_summary_hdl4');
  const overviewHeader5Content = tA11y('ek_reg_summary_hdl5');
  const overviewHeader6Content = tA11y('ek_reg_summary_hdl6');
  const overviewHeader7Content = tA11y('ek_reg_summary_hdl7');
  const overviewHeader8Content = tA11y('ek_reg_summary_hdl8');
  const overviewText7Content = tA11y('ek_reg_summary_txt1');
  const overviewText8Content = tA11y('ek_reg_summary_txt2');
  const overviewbtnContent = tA11y('ek_reg_summary_btn1');

  // const
  const LegitimationCopyTextContent = replaceDynamicCmsContent(
    tA11y('ek_reg_summary_change_txt1'),
    '{LEGITIMATION_NAME}',
    `${displayPersonalForm.legitimationName}`
  );

  const getSpeedName = (tariffId) => {
    const tariff = staticContentData?.ek_tariff?.find((t) => t.id === tariffId);
    return tariff ? tariff.nameSpeed : '';
  };

  const tariffName = getTariffName(
    displayTariffForm?.chosenTariffName,
    getSpeedName(displayTariffForm?.chosenTariffId)
  );

  return (
    <>
      <ScrollToTop>
        <section className="min-dvh-100 overview-data-container">
          {/* Auto-Topup Deletion Success Notification */}
          {showDeleteSuccess && (
            <div
              className="position-fixed top-0 w-100 pt-32 d-flex justify-content-center"
              style={{ zIndex: 9999 }}>
              <div className="bg-white border border-primary rounded-3 d-flex align-items-center px-24 py-8">
                <Icons name="SUCCESS" width={24} height={24} colorType="dark" />
                <Ta11yText
                  tag="span"
                  textContent={tA11yTranslate('ek_global_snackbar_autotopup_dlt')}
                  className="ek-copy-16-medium text-ek-black ms-12"
                />
              </div>
            </div>
          )}
          <AppPageContentWrapper>
            <div className="w-100 pb-40">
              <NavigationPageTitle headingTag="h2" title="ek_reg_summary_hdl1" variant="sm" />
            </div>
            {/* Tarrif Data */}
            <div className="details-container">
              <div className="title-container d-flex align-items-center text-primary">
                <Icons name="USER_SHILD" width={24} height={24} colorType="dark" />
                <Ta11yText
                  tag="h3"
                  textContent={overviewHeader2Content}
                  className="ek-h3 m-0 px-3"
                />
              </div>
              <div className="details-text pt-20">
                <Ta11yText tag="p" textContent={tariffName} className="ek-copy-16-regular" />
              </div>
              <span className="w-100 border-bottom border-secondary d-flex my-40" />
            </div>
            {/* MNP Data */}
            {withMNP && (
              <div className="details-container">
                <div className="title-container d-flex align-items-center text-primary">
                  <Icons name="USER_SHILD" width={24} height={24} colorType="dark" />
                  <Ta11yText
                    tag="h3"
                    textContent={overviewHeader7Content}
                    className="ek-h3 m-0 px-3"
                  />
                </div>
                <div className="details-text pt-20">
                  <Ta11yText
                    tag="p"
                    textContent={overviewText7Content}
                    className="ek-copy-16-regular m-0"
                  />
                  <p className="ek-copy-16-regular">{`${displayPhoneForm.oldNumberPrefix} ${displayPhoneForm.oldNumber}`}</p>
                  <p className="ek-copy-16-regular">
                    {
                      simProvider.find(
                        (provider) => displayPhoneForm.currentProvider === provider.id
                      )?.value
                    }
                  </p>
                </div>
                <Link
                  label="ek_reg_summary_change_lnk4"
                  iconLeft={appIconsPath.EDIT}
                  linkStyle={`${appLinkStyle.PRIMARY} mt-20`}
                  linkSize={appLinkSize.DEFAULT}
                  onClick={() => setStep9SubStep(7)}
                />
                <span className="w-100 border-bottom border-secondary d-flex my-40" />
              </div>
            )}
            {/* Auto-Topup Data */}
            {withAutoTopup && (
              <div className="details-container">
                <div className="title-container d-flex align-items-center text-primary">
                  <Icons name="COINS" width={24} height={24} colorType="dark" />
                  <Ta11yText
                    tag="h3"
                    textContent={overviewHeader8Content}
                    className="ek-h3 m-0 px-3"
                  />
                </div>
                <div className="details-text pt-20">
                  <Ta11yText
                    tag="p"
                    textContent={overviewText8Content}
                    className="ek-copy-16-regular m-0 mb-16"
                  />
                  <Ta11yText
                    tag="p"
                    textContent={replaceDynamicCmsContent(
                      replaceDynamicCmsContent(
                        tA11y('ek_reg_summary_txt3'),
                        '{amount}',
                        `${parseToGermanPrice(mpsActivationTopupPayment.amount, false)}`
                      ),
                      '{paymentmethod}',
                      //  Use the same payment method name mapping as PaymentCallbackHandler
                      appMpsPaymentMethodType.find(
                        (m) => m.methodMapping === mpsActivationTopupPayment.paymentMethod
                      )?.apiValue ||
                        mpsActivationTopupPayment.paymentMethod ||
                        ''
                    )}
                    className="ek-copy-16-regular m-0"
                  />
                </div>
                <Link
                  label="ek_reg_summary_change_lnk6"
                  iconLeft={appIconsPath.CLOSE_RED}
                  linkStyle={`${appLinkStyle.PRIMARY} mt-20 text-ek-error`}
                  linkSize={appLinkSize.DEFAULT}
                  onClick={handleDeleteAutoTopup}
                />
                <span className="w-100 border-bottom border-secondary d-flex my-40" />
              </div>
            )}
            {/* Personal Data */}
            <div className="details-container">
              <div className="title-container d-flex align-items-center text-primary">
                <Icons name="USER_SHILD" width={24} height={24} colorType="dark" />
                <Ta11yText
                  tag="h3"
                  textContent={overviewHeader3Content}
                  className="ek-h3 m-0 px-3"
                />
              </div>
              <div className="details-text pt-20">
                <p className="ek-copy-16-regular mb-8">{displayPersonalForm.firstName}</p>
                <p className="ek-copy-16-regular mb-8">{displayPersonalForm.lastName}</p>
                <p className="ek-copy-16-regular">
                  {displayPersonalForm.birthDate
                    ? (() => {
                        // Try multiple date formats
                        let parsed = moment(displayPersonalForm.birthDate, 'DD.MM.YYYY');
                        if (!parsed.isValid()) {
                          parsed = moment(displayPersonalForm.birthDate, 'YYYY-MM-DD');
                        }
                        if (!parsed.isValid()) {
                          parsed = moment(displayPersonalForm.birthDate);
                        }
                        return parsed.isValid()
                          ? parsed.format('DD.MM.YYYY')
                          : displayPersonalForm.birthDate;
                      })()
                    : '-'}
                </p>
              </div>
              <div className="title-container d-flex align-items-center text-primary">
                <Link
                  label="ek_reg_summary_change_lnk1"
                  iconLeft={appIconsPath.EDIT}
                  linkStyle={`${appLinkStyle.PRIMARY} mt-20`}
                  linkSize={appLinkSize.DEFAULT}
                  onClick={() => setStep9SubStep(5)}
                />
              </div>
              <span className="w-100 border-bottom border-secondary d-flex my-40" />
            </div>
            {/* Address Data */}
            <div className="details-container">
              <div className="title-container d-flex align-items-center text-primary">
                <Icons name="MARKER1" width={24} height={24} colorType="dark" />
                <Ta11yText
                  tag="h3"
                  textContent={overviewHeader4Content}
                  className="ek-h3 m-0 px-3"
                />
              </div>
              <div className="details-text pt-20">
                <p className="ek-copy-16-regular mb-8">{displayPersonalForm.street}</p>
                <p className="ek-copy-16-regular mb-8">{displayPersonalForm.houseNumber}</p>
                <p className="ek-copy-16-regular mb-8">{displayPersonalForm.zip}</p>
                <p className="ek-copy-16-regular mb-8">{displayPersonalForm.city}</p>
                <p className="ek-copy-16-regular">
                  {
                    countries?.find((contry) => displayPersonalForm.countryCode === contry.id)
                      ?.value
                  }
                </p>
              </div>
              <div className="title-container d-flex align-items-center text-primary">
                <Link
                  label="ek_reg_summary_change_lnk2"
                  iconLeft={appIconsPath.EDIT}
                  linkStyle={`${appLinkStyle.PRIMARY} mt-20`}
                  linkSize={appLinkSize.DEFAULT}
                  onClick={() => setStep9SubStep(6)}
                />
              </div>
              <span className="w-100 border-bottom border-secondary d-flex my-40" />
            </div>
            {/* Legitimation Data */}
            {legitimationMethods.length > 1 && (
              <div className="details-container">
                <div className="title-container d-flex align-items-center text-primary">
                  <Icons name="USER_SHILD" width={24} height={24} colorType="dark" />
                  <Ta11yText
                    tag="h3"
                    textContent={overviewHeader5Content}
                    className="ek-h3 m-0 px-3"
                  />
                </div>
                <div className="details-text pt-20">
                  <Ta11yText
                    tag="p"
                    textContent={LegitimationCopyTextContent}
                    className="ek-copy-16-regular"
                  />
                </div>
                <div className="title-container d-flex align-items-center text-primary">
                  <Link
                    label="ek_reg_summary_change_lnk3"
                    iconLeft={appIconsPath.EDIT}
                    linkStyle={`${appLinkStyle.PRIMARY} mt-20`}
                    linkSize={appLinkSize.DEFAULT}
                    onClick={() => setStep9SubStep(8)}
                  />
                </div>
                <span className="w-100 border-bottom border-secondary d-flex my-40" />
              </div>
            )}
            {/* Email Section */}
            <div className="details-container">
              <div className="title-container d-flex align-items-center text-primary">
                <Icons name="ENVELOPE" width={24} height={24} colorType="dark" />
                <Ta11yText
                  tag="h3"
                  textContent={overviewHeader6Content}
                  className="ek-h3 m-0 px-3"
                />
              </div>
              <div className="details-text pt-20">
                <p className="ek-copy-16-regular">{displayPhoneForm.email}</p>
              </div>
              <div className="details-text pt-20 w-100">
                <CardInfobox
                  cardStyle={appCardStyle.SECONDARY}
                  cardSize={appInfoCardSize.DEFAULT}
                  cardIcon={appIconsPath.INFO}
                  text="ek_reg_summary_info_txt1"
                />
              </div>
              <span className="w-100 border-bottom border-secondary d-flex my-40" />
            </div>
            <div className="w-100 d-flex justify-content-center">
              <div className="col-xxl-5 col-lg-5 col-10 px-0">
                <ButtonPrimary
                  buttonType={appButtonType.PRIMARY}
                  buttonContent={overviewbtnContent}
                  onClick={() => {
                    setStep9SubStep(1);
                  }}
                  isLoading={isLoading}
                />
              </div>
            </div>
            <div className="space-40" />
            <CardSupport />
            <div className="d-flex justify-content-center mt-32">
              <ButtonCancelActivation />
            </div>
          </AppPageContentWrapper>
        </section>
      </ScrollToTop>
    </>
  );
}

export default OverviewData;
